import React from 'react';
import { Box, Container, Typography, Card, CardContent, Avatar, List, ListItem, ListItemAvatar, ListItemText, Chip, LinearProgress, Paper } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const Leagues: React.FC = () => {
  const { user } = useAuth();

  // Mock leaderboard data
  const leaderboard = [
    { rank: 1, username: 'GreenThumb22', xp: 2450, avatar: '🌿', trend: 'up' },
    { rank: 2, username: 'PlantMaster', xp: 2380, avatar: '🌺', trend: 'stable' },
    { rank: 3, username: 'GardenGuru', xp: 2290, avatar: '🌻', trend: 'up' },
    { rank: 4, username: user?.username || 'You', xp: 2150, avatar: '🌱', trend: 'up', isCurrentUser: true },
    { rank: 5, username: 'VeggieKing', xp: 2100, avatar: '🥕', trend: 'down' },
    { rank: 6, username: 'FlowerPower', xp: 2050, avatar: '🌸', trend: 'stable' },
    { rank: 7, username: 'HerbWhisperer', xp: 1980, avatar: '🌿', trend: 'down' },
    { rank: 8, username: 'CropCircle', xp: 1920, avatar: '🌾', trend: 'down' },
  ];

  const leagues = [
    { name: 'Bronze', minXP: 0, color: '#CD7F32' },
    { name: 'Silver', minXP: 1000, color: '#C0C0C0' },
    { name: 'Gold', minXP: 2000, color: '#FFD700' },
    { name: 'Platinum', minXP: 3000, color: '#E5E4E2' },
    { name: 'Diamond', minXP: 5000, color: '#B9F2FF' },
  ];

  const currentLeague = leagues.find(league => (user?.gamification?.xp || 0) >= league.minXP) || leagues[0];
  const nextLeague = leagues[leagues.indexOf(currentLeague) + 1];
  const progressToNext = nextLeague 
    ? ((user?.gamification?.xp || 0) - currentLeague.minXP) / (nextLeague.minXP - currentLeague.minXP) * 100
    : 100;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        pt: 3, 
        pb: 3,
        background: `linear-gradient(135deg, ${currentLeague.color} 0%, #22c55e 100%)`
      }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <EmojiEventsIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {currentLeague.name} League
              </Typography>
              <Typography variant="body2">
                Week 12 • Top 30%
              </Typography>
            </Box>
          </Box>

          {/* Progress to next league */}
          {nextLeague && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  {user?.gamification?.xp || 0} XP
                </Typography>
                <Typography variant="body2">
                  {nextLeague.minXP} XP to {nextLeague.name}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressToNext} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white'
                  }
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {/* Zone Indicators */}
        <Box display="flex" gap={2} mb={3}>
          <Paper sx={{ p: 2, flex: 1, bgcolor: '#4ade80', color: 'white' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <ArrowUpwardIcon />
              <Box>
                <Typography variant="subtitle2">Promotion Zone</Typography>
                <Typography variant="body2">Top 3 players</Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, bgcolor: '#f87171', color: 'white' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <ArrowDownwardIcon />
              <Box>
                <Typography variant="subtitle2">Demotion Zone</Typography>
                <Typography variant="body2">Bottom 3 players</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Leaderboard */}
        <Typography variant="h6" gutterBottom>
          This Week's Rankings
        </Typography>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {leaderboard.map((player, index) => (
            <ListItem 
              key={player.rank}
              sx={{ 
                bgcolor: player.isCurrentUser ? 'primary.light' : 'transparent',
                borderLeft: index < 3 ? '4px solid #4ade80' : index >= leaderboard.length - 3 ? '4px solid #f87171' : 'none',
                mb: 0.5
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: player.isCurrentUser ? 'primary.main' : 'grey.300' }}>
                  {player.rank}
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6">{player.avatar}</Typography>
                    <Typography variant="body1" fontWeight={player.isCurrentUser ? 'bold' : 'normal'}>
                      {player.username}
                    </Typography>
                    {player.isCurrentUser && <Chip label="YOU" size="small" color="primary" />}
                  </Box>
                }
                secondary={`${player.xp} XP this week`}
              />
              <Box display="flex" alignItems="center">
                {player.trend === 'up' && <ArrowUpwardIcon color="success" />}
                {player.trend === 'down' && <ArrowDownwardIcon color="error" />}
              </Box>
            </ListItem>
          ))}
        </List>

        {/* League Info */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              League Rewards
            </Typography>
            <Box display="flex" gap={2}>
              <Chip icon={<Typography>🥇</Typography>} label="+100 XP" />
              <Chip icon={<Typography>🥈</Typography>} label="+50 XP" />
              <Chip icon={<Typography>🥉</Typography>} label="+25 XP" />
            </Box>
            <Typography variant="body2" color="text.secondary" mt={2}>
              League resets every Sunday at midnight
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Leagues;